import React from 'react';
import Icon from 'components/AppIcon';

/**
 * EmptyState — shared component for empty table/card areas.
 *
 * Props:
 *  icon          — Lucide icon name string (e.g. "Inbox") or a React node
 *  title         — bold heading
 *  description   — subtle sub-text
 *  ctaLabel      — primary CTA button label
 *  ctaAction     — primary CTA onClick handler
 *  secondaryCtaLabel   — optional secondary link label
 *  secondaryCtaAction  — optional secondary link onClick handler
 *  variant       — 'page' (full-height centered) | 'inline' (compact, for table cells)
 *  colSpan       — number of columns to span when used inside <tbody> (default 12)
 */
export default function EmptyState({
  icon = 'Inbox',
  title,
  description,
  ctaLabel,
  ctaAction,
  secondaryCtaLabel,
  secondaryCtaAction,
  variant = 'inline',
  colSpan = 12,
}) {
  const isPage = variant === 'page';

  const content = (
    <div
      className={`flex flex-col items-center justify-center text-center ${
        isPage ? 'py-24 px-6' : 'py-12 px-6'
      }`}
    >
      {/* Icon */}
      <div
        className={`flex items-center justify-center rounded-full mb-4 ${
          isPage
            ? 'w-16 h-16 bg-muted' :'w-12 h-12 bg-muted'
        }`}
      >
        {typeof icon === 'string' ? (
          <Icon
            name={icon}
            size={isPage ? 28 : 22}
            color="var(--color-muted-foreground)"
            strokeWidth={1.5}
          />
        ) : (
          icon
        )}
      </div>

      {/* Title */}
      {title && (
        <p
          className={`font-semibold text-foreground mb-1 ${
            isPage ? 'text-base' : 'text-sm'
          }`}
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {title}
        </p>
      )}

      {/* Description */}
      {description && (
        <p
          className={`text-muted-foreground max-w-xs ${
            isPage ? 'text-sm mb-5' : 'text-xs mb-4'
          }`}
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {description}
        </p>
      )}

      {/* Primary CTA */}
      {ctaLabel && ctaAction && (
        <button
          onClick={ctaAction}
          className={`inline-flex items-center gap-1.5 font-medium rounded-lg transition-colors ${
            isPage
              ? 'px-4 py-2 text-sm bg-accent text-white hover:bg-accent/90' :'px-3 py-1.5 text-xs bg-accent text-white hover:bg-accent/90'
          }`}
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {ctaLabel}
        </button>
      )}

      {/* Secondary CTA */}
      {secondaryCtaLabel && secondaryCtaAction && (
        <button
          onClick={secondaryCtaAction}
          className={`mt-2 text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors ${
            isPage ? 'text-xs' : 'text-xs'
          }`}
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {secondaryCtaLabel}
        </button>
      )}
    </div>
  );

  // When used inside a <tbody>, wrap in <tr><td>
  if (variant === 'inline' || variant === 'page') {
    return (
      <tr>
        <td colSpan={colSpan} className="p-0">
          {content}
        </td>
      </tr>
    );
  }

  return content;
}

/**
 * EmptyStateBlock — standalone div version (not inside a table).
 * Use this for card/panel empty states outside of <table> elements.
 */
export function EmptyStateBlock({
  icon = 'Inbox',
  title,
  description,
  ctaLabel,
  ctaAction,
  secondaryCtaLabel,
  secondaryCtaAction,
  compact = false,
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${
        compact ? 'py-6 px-4' : 'py-10 px-6'
      }`}
    >
      <div
        className={`flex items-center justify-center rounded-full mb-3 ${
          compact ? 'w-10 h-10 bg-muted' : 'w-12 h-12 bg-muted'
        }`}
      >
        {typeof icon === 'string' ? (
          <Icon
            name={icon}
            size={compact ? 18 : 22}
            color="var(--color-muted-foreground)"
            strokeWidth={1.5}
          />
        ) : (
          icon
        )}
      </div>

      {title && (
        <p
          className="text-sm font-semibold text-foreground mb-1"
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {title}
        </p>
      )}

      {description && (
        <p
          className="text-xs text-muted-foreground max-w-xs mb-3"
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {description}
        </p>
      )}

      {ctaLabel && ctaAction && (
        <button
          onClick={ctaAction}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {ctaLabel}
        </button>
      )}

      {secondaryCtaLabel && secondaryCtaAction && (
        <button
          onClick={secondaryCtaAction}
          className="mt-2 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {secondaryCtaLabel}
        </button>
      )}
    </div>
  );
}
